import type { OpaqueVideoData } from '@lumen5/luminary';
import type Luminary from '@lumen5/luminary';

import { getSceneDesignConfig } from '../lint/sceneDesignDefaults';

import { computeSceneShortIds } from './scene-addressing';

/**
 * Every way a video's author changed each scene relative to the scene design it was built from:
 * assets they added (stickers, extra labels, images), assets they removed, and every styling or
 * layout property they moved off the design's default.
 *
 * The annotation's `(changed from design default X)` markers cover a few properties (size, text
 * colour); this covers all of them, which is what tells "the format needs this" apart from "the
 * template happens to do this" when a recipe is written from a video.
 */

type AssetNode = Record<string, unknown>;

/** Content and identity rather than styling: these differ per video and are never a design change. */
const CONTENT_KEYS: ReadonlySet<string> = new Set([
    'id',
    'parentId',
    'text',
    'textHighlights',
    'sourceData',
    'children',
    'createdFrom',
    'creationSource',
    'playbackRegions',
    'transformMatrix',
]);
/** Filters and masks are named too, but belong to an asset and are compared as part of it. */
const NESTED_CLASS_TYPE = /(Filter|Mask)$/;
const MAX_VALUE_CHARS = 80;

export interface AssetChange {
    asset: string;
    property: string;
    designValue: unknown;
    videoValue: unknown;
}

export interface SceneDesignDiff {
    added: AssetNode[];
    removed: string[];
    changed: AssetChange[];
}

const isObject = (value: unknown): value is AssetNode =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Every named asset in a scene or design tree. A scene and each of its subscenes reuse the same asset
 * names, so subscene assets are keyed `subscene N/<name>`; the design has the same layout, which keeps
 * the two comparable key for key.
 */
export function collectNamedAssets(root: unknown): Map<string, AssetNode> {
    const assets = new Map<string, AssetNode>();
    const walk = (node: unknown, scope: string): void => {
        if (Array.isArray(node)) {
            node.forEach((child) => walk(child, scope));
            return;
        }
        if (!isObject(node)) {
            return;
        }
        const { name, classType } = node;
        if (
            typeof name === 'string' &&
            typeof classType === 'string' &&
            !NESTED_CLASS_TYPE.test(classType)
        ) {
            assets.set(`${scope}${name}`, node);
        }
        for (const [key, child] of Object.entries(node)) {
            if (key === 'subslides' && Array.isArray(child)) {
                child.forEach((subslide, index) => walk(subslide, `subscene ${index + 1}/`));
            } else {
                walk(child, scope);
            }
        }
    };
    walk(root, '');
    return assets;
}

/** Bookkeeping stripped and keys sorted, so equal styling compares equal however it was saved. */
function normalize(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map(normalize);
    }
    if (isObject(value)) {
        return Object.fromEntries(
            Object.keys(value)
                .filter((key) => !CONTENT_KEYS.has(key) && key !== 'name')
                .sort()
                .map((key) => [key, normalize(value[key])]),
        );
    }
    return value;
}

const same = (a: unknown, b: unknown): boolean =>
    JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));

/**
 * Property-level differences between an asset and its design default. A property missing on either
 * side is skipped: the editor fills some in on save (morph timing, sfx, opacity 1) and drops others
 * that hold their default, so neither says anything about the author.
 */
export function diffAsset(asset: AssetNode, design: AssetNode, assetName: string): AssetChange[] {
    const changes: AssetChange[] = [];
    const walk = (current: unknown, original: unknown, path: string): void => {
        if (
            original === undefined ||
            original === null ||
            current === undefined ||
            current === null ||
            same(current, original)
        ) {
            return;
        }
        if (isObject(current) && isObject(original)) {
            for (const [key, originalValue] of Object.entries(original)) {
                if (!CONTENT_KEYS.has(key)) {
                    walk(current[key], originalValue, path ? `${path}.${key}` : key);
                }
            }
            return;
        }
        // Same-length lists (animation steps, graphics layers) are compared item by item, so the
        // change names the one value that moved instead of reprinting two near-identical arrays.
        if (
            Array.isArray(current) &&
            Array.isArray(original) &&
            current.length === original.length
        ) {
            current.forEach((item, index) => walk(item, original[index], `${path}[${index}]`));
            return;
        }
        changes.push({
            asset: assetName,
            property: path,
            designValue: original,
            videoValue: current,
        });
    };
    walk(asset, design, '');
    return changes;
}

export function diffSceneAgainstDesign(scene: unknown, design: unknown): SceneDesignDiff {
    const sceneAssets = collectNamedAssets(scene);
    const designAssets = collectNamedAssets(design);
    return {
        added: [...sceneAssets.entries()]
            .filter(([key]) => !designAssets.has(key))
            .map(([key, asset]) => ({ ...asset, name: key })),
        removed: [...designAssets.keys()].filter((key) => !sceneAssets.has(key)),
        changed: [...sceneAssets.entries()].flatMap(([key, asset]) => {
            const designAsset = designAssets.get(key);
            return designAsset ? diffAsset(asset, designAsset, key) : [];
        }),
    };
}

const formatValue = (value: unknown): string => {
    const text = JSON.stringify(normalize(value)) ?? 'unset';
    return text.length > MAX_VALUE_CHARS ? `${text.slice(0, MAX_VALUE_CHARS)}…` : text;
};

/** A short description of an added asset: its kind, what it shows, and where it sits. */
function describeAddedAsset(asset: AssetNode): string {
    const sourceData = isObject(asset.sourceData) ? asset.sourceData : {};
    const text = typeof asset.text === 'string' ? asset.text.trim() : '';
    const label =
        (text && JSON.stringify(text)) ||
        (typeof sourceData.title === 'string' && sourceData.title) ||
        (typeof sourceData.type === 'string' && sourceData.type) ||
        '';
    const transformation = isObject(asset.transformation) ? asset.transformation : {};
    const position = isObject(transformation.position) ? transformation.position : {};
    const at =
        typeof position.x === 'number' && typeof position.y === 'number'
            ? ` at (${Math.round(position.x)}, ${Math.round(position.y)})`
            : '';
    return `${String(asset.name)} (${String(asset.classType)}${label ? `: ${label}` : ''})${at}`;
}

export function formatSceneDesignDiff(diff: SceneDesignDiff): string[] {
    const lines: string[] = [];
    if (diff.added.length > 0) {
        lines.push(`- Added, not in the design: ${diff.added.map(describeAddedAsset).join('; ')}`);
    }
    if (diff.removed.length > 0) {
        lines.push(`- Removed from the design: ${diff.removed.join(', ')}`);
    }
    for (const change of diff.changed) {
        lines.push(
            `- ${change.asset} ${change.property}: ${formatValue(change.designValue)} -> ${formatValue(change.videoValue)}`,
        );
    }
    return lines;
}

/**
 * The section for a loaded video: each scene (by the same `scene_id` the annotation uses) diffed
 * against the scene design config it was built from. Scenes left exactly as designed are listed as
 * such; a scene whose design config can't be read is left out. Empty when no scene could be compared.
 */
export function describeDesignChanges(luminary: Luminary, videoData: OpaqueVideoData): string {
    const slides = ((videoData as { slides?: unknown[] }).slides ?? []).filter(isObject);
    const shortIds = computeSceneShortIds(luminary.scenes.map((scene) => scene.id));
    const blocks: string[] = [];
    const unchanged: string[] = [];
    for (const scene of luminary.scenes) {
        const slide = slides.find((candidate) => candidate.id === scene.id);
        const design = getSceneDesignConfig(scene);
        if (!slide || design === null) {
            continue;
        }
        const label = `scene_id ${shortIds.get(scene.id) ?? scene.id} (${scene.sceneDesign})`;
        const lines = formatSceneDesignDiff(diffSceneAgainstDesign(slide, design));
        if (lines.length === 0) {
            unchanged.push(label);
        } else {
            blocks.push([`### ${label}`, ...lines].join('\n'));
        }
    }
    if (blocks.length === 0 && unchanged.length === 0) {
        return '';
    }
    return [
        '## Changes from the scene designs',
        "_Everything the author changed relative to each scene's design: assets added or removed, and every property moved off the design's default. Anything not listed is the design's own default, which a different template supplies on its own._",
        ...blocks,
        ...(unchanged.length > 0 ? [`Left exactly as designed: ${unchanged.join(', ')}`] : []),
    ].join('\n\n');
}
